import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import Link from '@atlaskit/link';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Flex, Inline, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from 'react-intl-next';

import SmartCard from '../../common/smart-card';
import { getDefaultBbcWorkspacesPage, type WorkspaceItem } from '../../utils/bitbucket-workspaces';

import messages from './messages';

type ModalDialogProps = {
	title: string;
	closeModalDialog: () => void;
	items: WorkspaceItem[];
};

export const WorkspacesModalDialog = ({ title, closeModalDialog, items }: ModalDialogProps) => {
	const workspacesList = items.slice(0, 9); // we only want 10 workspaces.
	const { formatMessage } = useIntl();

	return (
		<ModalTransition>
			<Modal onClose={closeModalDialog} testId="workspaces-modal" width="large" height="auto">
				<ModalHeader hasCloseButton>
					<ModalTitle>
						<Box xcss={modalHeaderStyles}>
							<Heading size="large" testId="workspaces-modal-heading">
								{title}
							</Heading>
						</Box>
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<Box xcss={modalBodyStyles}>
						<Box xcss={modalListBoxStyles}>
							{workspacesList.map((item, key) => {
								return (
									<SmartCard
										key={key}
										bitbucketRedirectUrl={item.redirectUrl}
										workspaceName={item?.name || ''} // if it gets here, then it must have workspace name
										analyticEvent={{
											action: 'clicked',
											actionSubject: 'link',
											actionSubjectId: 'bbc-workspace-link',
											source: 'home-page-agents',
										}}
									/>
								);
							})}
						</Box>

						{items.length > 10 && (
							<Box xcss={modalInfoPanelStyles}>
								<Flex gap="space.100">
									<Inline alignBlock="start">
										<Box xcss={infoPanelIconStyles}>
											<InformationCircleIcon
												label="Information"
												color={token('color.icon.accent.blue')}
											/>
										</Box>
										<Box xcss={modalInfoPanelTextBoxStyles}>
											<Text weight="regular" size="medium">
												{formatMessage(messages.modalDescription)}
												<Link href={getDefaultBbcWorkspacesPage()} target="_blank">
													{formatMessage(messages.seeAllWorkspaces)}
												</Link>
											</Text>
										</Box>
									</Inline>
								</Flex>
							</Box>
						)}
					</Box>
				</ModalBody>
				<ModalFooter>
					<Button
						appearance="subtle"
						onClick={closeModalDialog}
						testId="workspaces-modal-close-button"
					>
						Close
					</Button>
				</ModalFooter>
			</Modal>
		</ModalTransition>
	);
};

const modalHeaderStyles = xcss({
	paddingTop: 'space.300',
	paddingRight: 'space.300',
	paddingBottom: 'space.200',
	paddingLeft: 'space.300',
	gap: 'space.100',
});

const modalBodyStyles = xcss({
	paddingRight: 'space.300',
	paddingLeft: 'space.300',
});

const modalListBoxStyles = xcss({
	paddingTop: 'space.200',
	paddingBottom: 'space.200',
	gap: 'space.100',
});

const modalInfoPanelStyles = xcss({
	width: 'fill',
	backgroundColor: 'color.background.information',
	borderRadius: '3px',
	padding: 'space.100',
});

const modalInfoPanelTextBoxStyles = xcss({
	paddingRight: 'space.050',
	paddingLeft: 'space.050',
	gap: 'space.100',
});

const infoPanelIconStyles = xcss({
	padding: 'space.050',
});
